import { GenericAbortSignal } from 'axios';

import { Project } from '@/shared/types/Project';

import { Library, LibraryKind } from '../types/Library';
import { LIBRARY_API_URL } from './constants';
import { get, post } from './requestMethods';

const LANDING_API_URL = 'https://preproducts.rask.ai';

export type UploadVideoByLinkRequestData = {
  kind?: LibraryKind;
  link: string;
};

export type LandingMediaResponse = {
  content_type?: string;
  duration?: null | number;
  file_name?: string;
  file_size?: number;
  media_kind?: 'audio' | 'video';
  preview_url?: null | string;
  source_lang?: null | string;
  target_lang?: null | string;
  url: string;
};

type VideoInfoResponse = {
  title: null | string;
  duration_seconds: null | number;
  preview: null | string;
};

export type LinkedVideoInfo = {
  title: null | string;
  duration_seconds: null | number;
  preview: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isNullableString = (value: unknown): value is null | string => value === null || typeof value === 'string';

const isNullablePositiveFiniteNumber = (value: unknown): value is null | number =>
  value === null || (typeof value === 'number' && Number.isFinite(value) && value > 0);

export const adaptVideoInfoResponse = (value: unknown): LinkedVideoInfo => {
  if (
    !isRecord(value) ||
    !('duration_seconds' in value) ||
    !('preview' in value) ||
    !('title' in value) ||
    !isNullablePositiveFiniteNumber(value.duration_seconds) ||
    !isNullableString(value.preview) ||
    !isNullableString(value.title)
  ) {
    throw Object.assign(new Error('Video metadata is unavailable'), { status: 400 });
  }

  const response = value as VideoInfoResponse;

  return {
    title: response.title?.trim() || null,
    duration_seconds: response.duration_seconds,
    preview: response.preview?.trim() || '',
  };
};

const fetchLandingMedia = async (mediaId: string): Promise<LandingMediaResponse> => {
  const response = await fetch(`${LANDING_API_URL}/media/${mediaId}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch landing media: ${response.status}`);
  }

  return response.json();
};

export const LIBRARY_CALLS_API = {
  // Upload media by source URL
  uploadVideoByLink: (data: UploadVideoByLinkRequestData) => post<Library>(`${LIBRARY_API_URL}/media/link`, { data }),

  // Get media URL from landing backend
  getLandingMedia: fetchLandingMedia,

  // Get media file status by ID
  getLinkedMediaUploadStatus: (mediaId: string, signal?: GenericAbortSignal) =>
    get<Library>(`${LIBRARY_API_URL}/media/${mediaId}`, { signal }),

  // Get video metadata when uploading videos from Youtube
  getVideoInfo: (data: { url: Project['source_url'] }, signal?: GenericAbortSignal) =>
    get<unknown>(`${LIBRARY_API_URL}/info/video?link=${encodeURIComponent(data.url)}`, { signal }).then(
      adaptVideoInfoResponse,
    ),
};
